import { useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Download, FileDown } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface LecturerPrintCardProps {
  lecturer: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    skills?: string[];
    avatar_url?: string;
    status?: string;
  };
}

interface BulkExportProps {
  lecturers: Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    skills?: string[];
    avatar_url?: string;
    status?: string;
  }>;
}

const LecturerCardContent = ({ lecturer }: { lecturer: LecturerPrintCardProps['lecturer'] }) => (
  <div className="w-[105mm] h-[148mm] bg-white border-4 border-black p-5 flex flex-col items-center relative">
    {/* Logo */}
    <div className="w-full flex justify-center mb-5 border-b-2 border-black pb-4">
      <img 
        src="/global-it-logo.png" 
        alt="Global IT Academy" 
        className="w-[180px] h-auto"
        onError={(e) => e.currentTarget.style.display = 'none'}
      />
    </div>

    {/* Avatar */}
    <div className="mb-4 flex justify-center">
      {lecturer.avatar_url ? (
        <div className="w-[100px] h-[100px] rounded-full border-2 border-black overflow-hidden flex items-center justify-center">
          <img 
            src={lecturer.avatar_url}
            alt="Avatar"
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
          />
        </div>
      ) : (
        <div className="w-[100px] h-[100px] rounded-full border-2 border-black bg-white flex items-center justify-center text-4xl font-bold text-black">
          {lecturer.first_name[0]}{lecturer.last_name[0]}
        </div>
      )}
    </div>

    {/* Info section */}
    <div className="border-2 border-black rounded-lg p-4 w-full">
      <div className="text-lg font-bold text-black text-center mb-2 pb-2 border-b border-black">
        {lecturer.first_name} {lecturer.last_name}
      </div>
      
      <div className="text-sm text-black text-center mb-3 pb-2 border-b border-black">
        Դասախոս
      </div>

      {lecturer.skills && lecturer.skills.length > 0 && (
        <div className="flex items-start mb-2 text-sm py-1">
          <span className="font-bold text-black min-w-[90px]">Հմտություններ:</span>
          <span className="text-black ml-2">{lecturer.skills.join(', ')}</span>
        </div>
      )}

      {lecturer.status === 'approved' && (
        <div className="flex items-start text-sm py-1">
          <span className="font-bold text-black min-w-[90px]">Ստատուս:</span>
          <span className="text-black ml-2">Ակտիվ</span>
        </div>
      )}
    </div>

    {/* Footer */}
    <div className="mt-auto text-center text-black text-xs font-semibold pt-3 border-t-2 border-black w-full">
      Global IT Academy<br/>
      www.globalitacademy.am
    </div>
  </div>
);

export const BulkLecturerCardExport = ({ lecturers }: BulkExportProps) => {
  const bulkCardRef = useRef<HTMLDivElement>(null);

  const handleBulkDownloadPDF = async () => {
    if (!bulkCardRef.current || lecturers.length === 0) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const cardsPerPage = 4; // 2 columns x 2 rows
      const cardWidth = 105;
      const cardHeight = 148;
      
      for (let i = 0; i < lecturers.length; i++) {
        const pageIndex = Math.floor(i / cardsPerPage);
        const cardIndexOnPage = i % cardsPerPage;
        
        // Add new page if needed (but not for the first card)
        if (i > 0 && cardIndexOnPage === 0) {
          pdf.addPage();
        }

        // Get the card element for this lecturer
        const cardElement = bulkCardRef.current.children[i] as HTMLElement;
        if (!cardElement) continue;

        const canvas = await html2canvas(cardElement, {
          scale: 3,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
        });

        const imgData = canvas.toDataURL('image/png');
        
        // Calculate position (2 columns x 2 rows on A4)
        const col = cardIndexOnPage % 2;
        const row = Math.floor(cardIndexOnPage / 2);
        const x = col * cardWidth;
        const y = row * cardHeight;

        pdf.addImage(imgData, 'PNG', x, y, cardWidth, cardHeight);
      }

      pdf.save(`lecturer_cards_bulk_${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error('Error generating bulk PDF:', error);
    }
  };

  return (
    <>
      <Button
        onClick={handleBulkDownloadPDF}
        className="gap-2"
      >
        <FileDown className="h-4 w-4" />
        Ներբեռնել բոլոր քարտերը (A4)
      </Button>

      {/* Hidden cards for PDF generation */}
      <div ref={bulkCardRef} className="fixed left-[-9999px] top-0">
        {lecturers.map((lecturer, index) => (
          <div key={lecturer.id || index}>
            <LecturerCardContent lecturer={lecturer} />
          </div>
        ))}
      </div>
    </>
  );
}

export const LecturerPrintCard = ({ lecturer }: LecturerPrintCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleDownloadPDF = async () => {
    if (!cardRef.current) return;

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: null,
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a6'
      });

      const pageWidth = 105;
      const pageHeight = 148;

      pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
      pdf.save(`${lecturer.first_name}_${lecturer.last_name}_card.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownloadPDF}
        className="w-full justify-start"
      >
        <Download className="mr-2 h-4 w-4" />
        Ներբեռնել PDF
      </Button>

      {/* Hidden card for PDF generation */}
      <div ref={cardRef} className="fixed left-[-9999px] top-0">
        <LecturerCardContent lecturer={lecturer} />
      </div>
    </>
  );
};
